import React, { useState, useEffect } from 'react';
import { entriesAPI, companiesAPI } from '../services/api';
import TimeInput from '../components/TimeInput';
import CompanySelector from '../components/CompanySelector';
import Timer from '../components/Timer';
import styles from './TimeEntry.module.css';

export default function TimeEntry() {
  const getLocalDateString = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [companies, setCompanies] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showTimer, setShowTimer] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    company_id: '',
    date: getLocalDateString(),
    start_time: '09:00',
    end_time: '17:00',
    description: '',
    rate_type: 'standard',
  });

  // Load companies and entries on mount
  useEffect(() => {
    loadCompanies();
    loadEntries();

    // Refresh entries every 30 seconds
    const interval = setInterval(loadEntries, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await companiesAPI.getAll();
      setCompanies(response.data);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await entriesAPI.getWeekEntries(getLocalDateString());
      setEntries(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleTimeChange = (field, value) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!formData.company_id) {
      setError('Please select a company');
      return;
    }

    try {
      setLoading(true);
      await entriesAPI.createEntry({
        ...formData,
        company_id: parseInt(formData.company_id),
      });

      setMessage('Time entry created successfully!');
      setFormData({
        company_id: '',
        date: getLocalDateString(),
        start_time: '09:00',
        end_time: '17:00',
        description: '',
        rate_type: 'standard',
      });

      // Reload entries
      loadEntries();

      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create entry');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      await entriesAPI.deleteEntry(id);
      setMessage('Entry deleted successfully!');
      loadEntries();
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete entry');
    }
  };

  const handleTimerComplete = (timerData) => {
    setFormData({
      ...formData,
      start_time: timerData.timeString,
    });
    setShowTimer(false);
  };

  return (
    <div className={styles.container}>
      <h1>Create Time Entry</h1>

      {error && <div className={styles.error}>{error}</div>}
      {message && <div className={styles.success}>{message}</div>}

      {(() => {
        const selectedCompany = companies.find((c) => String(c.id) === String(formData.company_id));
        const hasSplitRates = Boolean(selectedCompany?.has_split_rates);
        return (
      <form onSubmit={handleSubmit} className={styles.form}>
        <CompanySelector
          companies={companies}
          selected={formData.company_id}
          onChange={(id) => setFormData({ ...formData, company_id: id, rate_type: 'standard' })}
        />

        <div className={styles.quickFields}>
          {hasSplitRates && (
            <div className={`${styles.formGroup} ${styles.compactField}`}>
              <label>Rate Type:</label>
              <div className={styles.rateSelector}>
                <button
                  type="button"
                  className={formData.rate_type === 'standard' ? styles.rateActive : styles.rateInactive}
                  onClick={() => setFormData({ ...formData, rate_type: 'standard' })}
                >
                  Standard
                </button>
                <button
                  type="button"
                  className={formData.rate_type === 'advanced' ? styles.rateActive : styles.rateInactive}
                  onClick={() => setFormData({ ...formData, rate_type: 'advanced' })}
                >
                  Advanced
                </button>
              </div>
            </div>
          )}

          <div className={`${styles.formGroup} ${styles.compactField} ${styles.dateField}`}>
            <label htmlFor="date">Date:</label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              required
            />
          </div>

          <TimeInput
            className={styles.compactTimeInput}
            label="Start Time"
            value={formData.start_time}
            onChange={(value) => handleTimeChange('start_time', value)}
          />

          <TimeInput
            className={styles.compactTimeInput}
            label="End Time"
            value={formData.end_time}
            onChange={(value) => handleTimeChange('end_time', value)}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">Description:</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="What work did you do?"
            rows="3"
          />
        </div>

        <div className={styles.timerToggle}>
          <button
            type="button"
            onClick={() => setShowTimer(!showTimer)}
            className={styles.btnSecondary}
          >
            {showTimer ? 'Hide Timer' : 'Show Timer'}
          </button>
        </div>

        {showTimer && <Timer onComplete={handleTimerComplete} />}

        <button type="submit" disabled={loading} className={styles.btnPrimary}>
          {loading ? 'Creating...' : 'Create Entry'}
        </button>
      </form>
        );
      })()}

      <h2>Today's Entries</h2>
      {loading && <p>Loading entries...</p>}
      {entries.length === 0 ? (
        <p>No entries yet. Create one above!</p>
      ) : (
        <div className={styles.entriesList}>
          {entries.map((entry) => (
            <div key={entry.id} className={styles.entryCard}>
              <div className={styles.entryHeader}>
                <strong>{entry.company_name}</strong>
                <span className={styles.entryDate}>{entry.date}</span>
              </div>
              <p>
                <strong>Time:</strong> {entry.start_time} - {entry.end_time}
              </p>
              {entry.rate_type && entry.rate_type !== 'standard' && (
                <p><strong>Rate:</strong> <span className={styles.rateTag}>{entry.rate_type.charAt(0).toUpperCase() + entry.rate_type.slice(1)}</span></p>
              )}
              <p>
                <strong>Description:</strong> {entry.description || 'N/A'}
              </p>
              <button
                onClick={() => handleDeleteEntry(entry.id)}
                className={styles.btnDelete}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
